import { Formiz, useForm } from "@formiz/core";
import { isEmail, isRequired } from "@formiz/validations";
import { useTranslation } from "@next-platform/client-common-core";
import { Spacer, Spinner } from "@nextui-org/react";
import React, { useCallback, useMemo } from "react";

import FormField from "@/components/common/FormField";
import { SpicyButton, SpicyInput, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

type RequestEmailChangeFormProps = {
  defaultEmail: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (email: string) => void;
  "data-testid"?: string;
};

const RequestEmailChangeForm = ({
  defaultEmail,
  isSubmitting,
  errorMessage,
  onSubmit,
  "data-testid": testId = "RequestEmailChangeForm",
}: RequestEmailChangeFormProps) => {
  const { t } = useTranslation();
  const emailForm = useForm();

  const handleValidSubmit = useCallback(
    (values?: object) => {
      const { email } = (values ?? {}) as { email?: string };
      onSubmit(email ?? "");
    },
    [onSubmit],
  );

  const emailValidations = useMemo(
    () => [
      { rule: isRequired(), message: t("user:updateEmail.emailRequired") },
      { rule: isEmail(), message: t("user:updateEmail.emailInvalid") },
    ],
    [t],
  );

  return (
    <div className="w-full" data-testid={testId}>
      <Text h3>
        <Trans i18nKey="user:updateEmail.heading" />
      </Text>
      <Spacer y={2} />
      <Text variant="md" type="opacity-60" className="block leading-7">
        <Trans i18nKey="user:updateEmail.emailDesc" />
      </Text>
      <Spacer y={3} />
      <Formiz connect={emailForm} onValidSubmit={handleValidSubmit}>
        <form className="flex w-full flex-col" noValidate onSubmit={emailForm.submit}>
          <div className="w-full">
            <FormField
              component={SpicyInput}
              variant="primary"
              color="primary"
              title={t("user:updateEmail.emailLabel")}
              name="email"
              defaultValue={defaultEmail}
              spacer={2}
              aria-labelledby="Email"
              data-testid={`${testId}-EmailInput`}
              validations={emailValidations}
            />
          </div>
          {errorMessage ? (
            <div className="mb-4 text-left" data-testid={`${testId}-Error`}>
              <Text type="error">{errorMessage}</Text>
            </div>
          ) : null}
          <SpicyButton
            variant="primary_large"
            color="primary_strong"
            type="submit"
            className="!w-full"
            aria-labelledby="update"
            data-testid={`${testId}-SubmitButton`}
            isDisabled={!emailForm.isValid || isSubmitting}
          >
            {isSubmitting ? <Spinner color="current" size="sm" /> : <Trans i18nKey="user:updateEmail.request.submit" />}
          </SpicyButton>
        </form>
      </Formiz>
    </div>
  );
};

export default React.memo(RequestEmailChangeForm);
